import { useEffect, useState } from 'react';

export interface CountdownState {
  hours: number;
  minutes: number;
  seconds: number;
  /** true when less than 6 hours remain */
  isUrgent: boolean;
  /** true when the auction has ended */
  isDead: boolean;
}

/**
 * Reactive countdown hook. Updates every second and returns hours/minutes/seconds
 * as well as semantic flags `isUrgent` (< 6h remaining) and `isDead` (expired).
 */
export function useCountdown(endsAt: number): CountdownState {
  const [remaining, setRemaining] = useState(() => endsAt - Date.now());

  useEffect(() => {
    const id = setInterval(() => setRemaining(endsAt - Date.now()), 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  const total = Math.max(0, remaining);
  return {
    hours: Math.floor(total / (1000 * 60 * 60)),
    minutes: Math.floor((total % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((total % (1000 * 60)) / 1000),
    isUrgent: total > 0 && total < 1000 * 60 * 60 * 6,
    isDead: total === 0,
  };
}
